import type { SelectOption } from '@/components/common/Select';
import type { QuoteStatus } from '@/types';
import { titleCase } from '@/utils';

/**
 * The whole `QuoteRequest.status` vocabulary, in the ladder's order with the
 * legacy values after it.
 *
 * ONE COPY, because there were two screens disagreeing about it: the detail
 * page listed all eleven while the list page's filter offered six, five of them
 * legacy -- so `pending`, `quote_shared` and all three KYC rungs, which is where
 * most live quotes now sit, could not be filtered for at all.
 *
 * Mirrors `QuoteRequest.STATUS_CHOICES` (backend/apps/quotes/models.py:37).
 * WHICH moves between these are legal is deliberately NOT encoded here: that
 * table is `apps/quotes/lifecycle.py::TRANSITIONS` and the server refuses an
 * illegal move with a message naming both ends. A second copy in the browser
 * would be a second table to keep in step and it would not be the one deciding.
 */
export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  pending: 'Pending',
  quote_shared: 'Quote Shared',
  kyc_pending_approval: 'KYC Pending Approval',
  kyc_rejected: 'KYC Rejected',
  kyc_approved: 'KYC Approved',
  completed: 'Completed',
  draft: 'Draft',
  submitted: 'Submitted',
  processing: 'Processing',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

/**
 * The six rungs of the current KYC-gated lifecycle, in the owner's order.
 * Everything after them in `QUOTE_STATUS_LABELS` is legacy.
 */
export const QUOTE_LIFECYCLE_STATUSES: QuoteStatus[] = [
  'pending',
  'quote_shared',
  'kyc_pending_approval',
  'kyc_rejected',
  'kyc_approved',
  'completed',
];

/**
 * The five values no new quote can reach, but which live rows still carry --
 * PROD holds `processing`, `cancelled`, `expired` and `draft` quotes today. They
 * stay filterable (a broker still has to find those quotes) but under their own
 * heading, so the dropdown is honest about which half of it is live. Decision 10.
 */
export const QUOTE_LEGACY_STATUSES: QuoteStatus[] = [
  'draft',
  'submitted',
  'processing',
  'expired',
  'cancelled',
];

/** Heading the legacy block sits under in a status dropdown. */
export const QUOTE_LEGACY_STATUS_GROUP = 'Older statuses';

/** A status's human label, falling back to a title-cased raw value so a status
 *  added server-side still reads as words rather than `kyc_foo`. */
export function quoteStatusLabel(status: string): string {
  return QUOTE_STATUS_LABELS[status as QuoteStatus] ?? titleCase(status);
}

/** Every status as a flat `<option>` list -- what the detail page's "change
 *  status" control offers. No blank entry: that control always has a value. */
export const QUOTE_STATUS_OPTIONS: SelectOption[] = (
  Object.keys(QUOTE_STATUS_LABELS) as QuoteStatus[]
).map((value) => ({ value, label: QUOTE_STATUS_LABELS[value] }));

/**
 * The status FILTER's options: "All Status", the six live rungs, then the
 * legacy five under their own heading.
 *
 * Each `value` is sent verbatim as `?status=` and every one of them is a
 * declared `QuoteRequest.STATUS_CHOICES` key, so django-filter's generated
 * ChoiceFilter accepts it (an undeclared value 400s, which is exactly what a
 * label-only entry here would produce).
 */
export const QUOTE_STATUS_FILTER_OPTIONS: SelectOption[] = [
  { value: '', label: 'All Status' },
  ...QUOTE_LIFECYCLE_STATUSES.map((value) => ({ value, label: QUOTE_STATUS_LABELS[value] })),
  ...QUOTE_LEGACY_STATUSES.map((value) => ({
    value,
    label: QUOTE_STATUS_LABELS[value],
    group: QUOTE_LEGACY_STATUS_GROUP,
  })),
];
